import { z } from "zod";
import { folderNameProblem, MAX_FOLDER_DEPTH, normalizeFolderName } from "./folders.ts";
import { WIKI_MARKDOWN_MAX, wikiMarkdownSchema, wikiPageTypeSchema } from "./wiki.ts";
import {
  WIKI_CHANGE_SET_BYTES_MAX,
  WIKI_CHANGE_SET_ITEMS_MAX,
  WIKI_CHANGE_SET_TEXT_MAX,
  WIKI_FOLDER_ITEMS_MAX,
  wikiChangeItemStatusSchema,
  wikiChangeOpSchema,
  wikiChangeSetStatusSchema,
  wikiCheckSkipReasonSchema,
} from "./wiki-change-sets.ts";
import { wikiThreadKindSchema } from "./wiki-threads.ts";

// ── The agent's wiki tools (openspec change `wiki-collaboration`, design D4, D6) ──────────────
//
// `wiki_tree`, `wiki_read`, `wiki_write` and `wiki_changes` are internal tools of a project
// conversation. Their inputs and outputs name pages and folders by title and path, never by ID:
// the output schemas below are strict, so a result carrying an identifier field fails them.

export const WIKI_TREE_TOOL = "wiki_tree";
export const WIKI_READ_TOOL = "wiki_read";
export const WIKI_WRITE_TOOL = "wiki_write";
export const WIKI_CHANGES_TOOL = "wiki_changes";
export const WIKI_AGENT_TOOLS = [
  WIKI_TREE_TOOL,
  WIKI_READ_TOOL,
  WIKI_WRITE_TOOL,
  WIKI_CHANGES_TOOL,
] as const;

/** Operations one `wiki_write` call applies (D6). */
export const WIKI_WRITE_OPERATIONS_MAX = 20;
/** Find-and-replace edits one `edit` operation carries (D6). */
export const WIKI_WRITE_EDITS_MAX = 20;
/** Pages and folders one `wiki_tree` call lists (D4). */
export const WIKI_TREE_PAGES_MAX = 500;
export const WIKI_TREE_FOLDERS_MAX = 200;
/** Titles of linking pages one `wiki_read` result lists. */
export const WIKI_READ_BACKLINKS_MAX = 50;

/** The separator of a path: folder names, then (for a page) its title. */
export const WIKI_PATH_SEPARATOR = " / ";

/** A path's folder names (trimmed, NFC), or null when a segment is not a valid folder name. */
export function parseFolderPath(path: string): string[] | null {
  const trimmed = path.trim();
  if (trimmed === "") {
    return [];
  }
  const segments = trimmed.split("/").map(normalizeFolderName);
  if (segments.length > MAX_FOLDER_DEPTH || segments.some((s) => folderNameProblem(s) !== null)) {
    return null;
  }
  return segments;
}
export const formatWikiPath = (segments: readonly string[]) => segments.join(WIKI_PATH_SEPARATOR);

const folderPathSchema = z
  .string()
  .max(1000)
  .refine((path) => parseFolderPath(path) !== null, {
    message: `A folder path is folder names joined by " / ", at most ${MAX_FOLDER_DEPTH} levels, each 1 to 120 characters without / or \\`,
  })
  .describe('Folder names from the wiki root joined by " / ", e.g. "Operations / Pumps"');
const nonRootFolderPathSchema = folderPathSchema.refine((path) => path.trim() !== "", {
  message: "Name a folder, not the wiki root",
});
const pageRefSchema = z
  .string()
  .trim()
  .min(1)
  .max(1000)
  .describe('A page title, or its path ("Operations / Pumps / Pump P-200")');
const titleSchema = z.string().trim().min(1).max(200);

export const wikiFindReplaceSchema = z.object({
  find: z.string().min(1).max(WIKI_MARKDOWN_MAX).describe("Exact text, unique in the page"),
  replace: z.string().max(WIKI_MARKDOWN_MAX),
});

export const wikiWriteOperationSchema = z.discriminatedUnion("op", [
  z.object({
    op: z.literal("create"),
    path: folderPathSchema.optional(),
    title: titleSchema,
    pageType: wikiPageTypeSchema.optional(),
    markdown: wikiMarkdownSchema.min(1),
  }),
  z
    .object({
      op: z.literal("edit"),
      page: pageRefSchema,
      markdown: wikiMarkdownSchema.min(1).optional().describe("The whole new page"),
      edits: z
        .array(wikiFindReplaceSchema)
        .min(1)
        .max(WIKI_WRITE_EDITS_MAX)
        .optional()
        .describe("Exact find-and-replace edits, applied in order"),
    })
    .refine((o) => (o.markdown === undefined) !== (o.edits === undefined), {
      message: "An edit carries either markdown or edits",
    }),
  z.object({ op: z.literal("rename"), page: pageRefSchema, title: titleSchema }),
  z.object({
    op: z.literal("move"),
    page: pageRefSchema,
    folder: folderPathSchema.describe('The target folder path ("" for the wiki root)'),
  }),
  z.object({ op: z.literal("delete"), page: pageRefSchema }),
  z.object({ op: z.literal("folder_create"), path: nonRootFolderPathSchema }),
  z.object({
    op: z.literal("folder_rename"),
    path: nonRootFolderPathSchema,
    name: z
      .string()
      .max(480)
      .refine((n) => folderNameProblem(normalizeFolderName(n)) === null, {
        message: "A folder name has 1 to 120 characters, without / or \\",
      }),
  }),
  z.object({
    op: z.literal("folder_move"),
    path: nonRootFolderPathSchema,
    into: folderPathSchema.describe('The new parent folder path ("" for the wiki root)'),
  }),
  z.object({ op: z.literal("folder_delete"), path: nonRootFolderPathSchema }),
  z
    .object({
      op: z.literal("drop"),
      page: pageRefSchema.optional(),
      path: nonRootFolderPathSchema.optional(),
    })
    .refine((o) => (o.page === undefined) !== (o.path === undefined), {
      message: "Drop names either a page or a folder path",
    }),
]);
export type WikiWriteOperation = z.infer<typeof wikiWriteOperationSchema>;

export const wikiWriteInputSchema = z.object({
  title: titleSchema.optional().describe("The change set's title (required on the first write)"),
  rationale: z
    .string()
    .max(WIKI_CHANGE_SET_TEXT_MAX)
    .optional()
    .describe("Why these changes, for the reviewers"),
  operations: z.array(wikiWriteOperationSchema).min(1).max(WIKI_WRITE_OPERATIONS_MAX),
});
export type WikiWriteInput = z.infer<typeof wikiWriteInputSchema>;

export const wikiTreeInputSchema = z.object({
  folder: folderPathSchema.optional(),
  depth: z.int().min(1).max(MAX_FOLDER_DEPTH).optional(),
});
export type WikiTreeInput = z.infer<typeof wikiTreeInputSchema>;

export const wikiReadInputSchema = z
  .object({
    title: titleSchema.optional(),
    path: pageRefSchema.optional(),
    version: z.int().min(1).optional(),
  })
  .refine((o) => o.title !== undefined || o.path !== undefined, {
    message: "Name the page by title or path",
  });
export type WikiReadInput = z.infer<typeof wikiReadInputSchema>;

export const wikiChangesInputSchema = z.object({});

// Outputs: strict objects, so an identifier field (`id`, `pageId`, …) fails the schema.

export const wikiTreeOutputSchema = z.strictObject({
  pages: z
    .array(
      z.strictObject({
        title: z.string(),
        path: z.string(),
        type: wikiPageTypeSchema,
        version: z.int().min(1),
        updated: z.string(),
      }),
    )
    .max(WIKI_TREE_PAGES_MAX),
  folders: z.array(z.strictObject({ path: z.string() })).max(WIKI_TREE_FOLDERS_MAX),
  truncated: z.boolean(),
  hint: z.string().optional(),
});
export type WikiTreeOutput = z.infer<typeof wikiTreeOutputSchema>;

export const wikiReadOutputSchema = z.strictObject({
  title: z.string(),
  path: z.string(),
  type: wikiPageTypeSchema,
  version: z.int().min(1),
  currentVersion: z.int().min(1),
  markdown: z.string(),
  linkedFrom: z.array(z.string()).max(WIKI_READ_BACKLINKS_MAX),
});
export type WikiReadOutput = z.infer<typeof wikiReadOutputSchema>;

export const wikiChangeItemSummarySchema = z.strictObject({
  op: wikiChangeOpSchema,
  title: z.string(),
  path: z.string().optional(),
  status: wikiChangeItemStatusSchema,
  stale: z.boolean().optional(),
  rebased: z.boolean().optional(),
  notChecked: wikiCheckSkipReasonSchema.optional(),
});
export const wikiBudgetSchema = z.strictObject({
  items: z.int().min(0).max(WIKI_CHANGE_SET_ITEMS_MAX),
  bytes: z.int().min(0).max(WIKI_CHANGE_SET_BYTES_MAX),
  folderItems: z.int().min(0).max(WIKI_FOLDER_ITEMS_MAX),
});
/** Open threads `wiki_changes` returns at most, newest comments kept (D15). */
export const WIKI_AGENT_THREADS_MAX = 50;
export const WIKI_AGENT_THREAD_COMMENTS_MAX = 10;
export const wikiThreadForAgentSchema = z.strictObject({
  /** The item's title; absent for a thread on the change set itself. */
  item: z.string().optional(),
  kind: wikiThreadKindSchema,
  /** A change request addressed to the agent, not revised yet. */
  revisionRequested: z.boolean().optional(),
  comments: z
    .array(
      z.strictObject({
        /** `user`: the person the agent works for; `reviewer`: someone else; `system`. */
        from: z.enum(["user", "reviewer", "system"]),
        text: z.string(),
      }),
    )
    .max(WIKI_AGENT_THREAD_COMMENTS_MAX),
});
export const wikiChangesOutputSchema = z.strictObject({
  title: z.string(),
  rationale: z.string(),
  status: z.union([wikiChangeSetStatusSchema, z.literal("working")]),
  items: z.array(wikiChangeItemSummarySchema).max(WIKI_CHANGE_SET_ITEMS_MAX),
  hiddenItems: z.boolean(),
  mayPublish: z.boolean(),
  remaining: wikiBudgetSchema,
  /**
   * The open review threads on the set and its items the user sees (wiki-collaboration D15):
   * plain text, framed as reviewers' input, with no name or ID. Absent when there is none.
   */
  threads: z.array(wikiThreadForAgentSchema).max(WIKI_AGENT_THREADS_MAX).optional(),
  threadsNote: z.string().optional(),
});
export type WikiChangesOutput = z.infer<typeof wikiChangesOutputSchema>;

export const wikiWriteOutputSchema = z.strictObject({
  status: z.literal("written"),
  changeSet: z.strictObject({ title: z.string(), status: wikiChangesOutputSchema.shape.status }),
  items: z.array(wikiChangeItemSummarySchema).max(WIKI_CHANGE_SET_ITEMS_MAX),
  remaining: wikiBudgetSchema,
  note: z.string(),
});
export type WikiWriteOutput = z.infer<typeof wikiWriteOutputSchema>;

// ── The Wiki changes card (D8) ─────────────────────────────────────────────────────────────

/** The working change set's card, as the conversation shows it while the agent writes. */
export const wikiChangesCardSchema = z.object({
  changeSetId: z.uuid(),
  title: z.string(),
  status: z.union([wikiChangeSetStatusSchema, z.literal("working")]),
  pages: z.int().min(0),
  folders: z.int().min(0),
  stale: z.int().min(0),
  /** The turn's writes so far: a new value means the set changed (content-only edits too). */
  writes: z.int().min(0).optional(),
});
export type WikiChangesCard = z.infer<typeof wikiChangesCardSchema>;

/**
 * Streamed on the turn's first wiki write and after each later one, and stored with the message
 * (one per message, replaced by ID); clients without it ignore it.
 */
export const WIKI_CHANGES_DATA_NAME = "wiki-changes";
export const WIKI_CHANGES_PART = `data-${WIKI_CHANGES_DATA_NAME}` as const;
export const WIKI_CHANGES_PART_ID = "wiki-changes";
export const wikiChangesPartSchema = z.object({
  type: z.literal(WIKI_CHANGES_PART),
  id: z.literal(WIKI_CHANGES_PART_ID),
  data: wikiChangesCardSchema,
});
export type WikiChangesPart = z.infer<typeof wikiChangesPartSchema>;
